import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { MyPayReturnStatus } from "@/components/profile/MyPayTopupButton";

/**
 * Landing page after the customer returns from MyPay's hosted payment page.
 * The URL looks like `/wallet/mypay/return?custom=TL-TOPUP-...&cancel=1?`.
 *
 * IMPORTANT: This page is intentionally UX-only. It never credits the wallet —
 * only the signed webhook does. Here we just poll for the settlement outcome
 * so the customer sees success/failure feedback while the webhook lands.
 */
export default function MyPayReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const custom = params.get("custom");
  const cancelled = params.get("cancel") === "1";
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (!custom) navigate("/profile", { replace: true });
  }, [custom, navigate]);

  useEffect(() => {
    // Clear the stashed pending id — resolved either way now.
    if (resolved) { try { localStorage.removeItem("mypay:pending"); } catch { /* noop */ } }
  }, [resolved]);

  const succeeded = resolved?.credited || resolved?.status === "completed";
  const failed = resolved && (["failed", "cancelled", "timeout"].includes(String(resolved.status || "").toLowerCase()) || cancelled);

  return (
    <div className="min-h-screen bg-[var(--surface-alt)] flex items-center justify-center p-4" data-testid="mypay-return-page">
      <div className="max-w-md w-full bg-white rounded-3xl talabya-shadow-float p-6 text-center">
        {!resolved && !cancelled && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-light text-primary-brand flex items-center justify-center mx-auto mb-3">
              <Loader2 size={32} className="animate-spin" />
            </div>
            <h2 className="text-lg font-black" style={{ fontFamily: "Cairo" }}>ننتظر تأكيد الدفع من MyPay</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5 mb-4">قد يستغرق ذلك حتى 30 ثانية. لا تغلق الصفحة.</p>
            <MyPayReturnStatus custom={custom} onResolved={setResolved} />
          </>
        )}

        {cancelled && !resolved && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
              <XCircle size={36} />
            </div>
            <h2 className="text-lg font-black" style={{ fontFamily: "Cairo" }}>تم إلغاء عملية الدفع</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5 mb-5">يمكنك المحاولة مرة أخرى في أي وقت.</p>
          </>
        )}

        {succeeded && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={40} />
            </div>
            <h2 className="text-lg font-black text-emerald-700" style={{ fontFamily: "Cairo" }} data-testid="mypay-success">تم شحن محفظتك بنجاح</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5">
              تم إضافة <b>{resolved?.amount} د.ل</b> إلى رصيدك.
              {resolved?.gateway && <span className="mx-1 opacity-70">({resolved.gateway})</span>}
            </p>
          </>
        )}

        {failed && !succeeded && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
              <XCircle size={40} />
            </div>
            <h2 className="text-lg font-black text-red-700" style={{ fontFamily: "Cairo" }} data-testid="mypay-failed">لم تنجح عملية الدفع</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5">لم يتم خصم أي مبلغ. يمكنك إعادة المحاولة.</p>
          </>
        )}

        {(resolved || cancelled) && (
          <button
            onClick={() => navigate("/profile", { replace: true })}
            className="mt-6 w-full py-3 rounded-2xl bg-primary-brand hover:bg-blue-800 text-white font-black flex items-center justify-center gap-2"
            data-testid="mypay-back-btn"
          >
            العودة إلى المحفظة <ArrowRight size={16} className="rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}
